import React, { useCallback, useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AuthContext from "../../context/AuthContext";
import SearchableSelectField from "./SearchableSelectField";
import { fetchMccList, resolveMccByCode } from "./MccSelectField.thunk";

/**
 * MCC catalog select — loads paginated options from GET /mcc with search + scroll load-more.
 */
const MccSelectField = ({
  name = "acquirer.mccID",
  value = "",
  onChange,
  onBlur,
  label = "MCC",
  required = false,
  error = "",
  touched = false,
  disabled = false,
  helperText = "Merchant category code (ISO 18245)",
  sx = {},
}) => {
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const resolvedCodeRef = useRef(null);

  const {
    items,
    status,
    search,
    page,
    hasMore,
    resolvedByCode,
  } = useSelector((state) => state.mccSelectField);

  const loading = status === "loading";
  const loadingMore = status === "loadingMore";
  const resolvedOption = value ? resolvedByCode[value] : null;

  const ensureCatalogLoaded = useCallback(() => {
    if (!token || loading || loadingMore) return;
    if (items.length === 0) {
      dispatch(fetchMccList({ token, search: "", page: 1, append: false }));
    }
  }, [dispatch, token, items.length, loading, loadingMore]);

  const handleOpen = useCallback(() => {
    ensureCatalogLoaded();
  }, [ensureCatalogLoaded]);

  const handleSearch = useCallback(
    (term) => {
      if (!token) return;
      dispatch(
        fetchMccList({
          token,
          search: term,
          page: 1,
          append: false,
        }),
      );
    },
    [dispatch, token],
  );

  const handleLoadMore = useCallback(() => {
    if (!token || !hasMore || loading || loadingMore) return;
    dispatch(
      fetchMccList({
        token,
        search,
        page: page + 1,
        append: true,
      }),
    );
  }, [dispatch, token, hasMore, loading, loadingMore, search, page]);

  useEffect(() => {
    const trimmed = String(value || "").trim();
    if (!token || !trimmed) return;
    if (items.some((item) => item.id === trimmed)) return;
    if (resolvedByCode[trimmed]) return;
    if (resolvedCodeRef.current === trimmed) return;
    resolvedCodeRef.current = trimmed;
    dispatch(resolveMccByCode({ token, code: trimmed }));
  }, [value, token, items, resolvedByCode, dispatch]);

  const displayError = error;

  return (
    <SearchableSelectField
      name={name}
      label={label}
      value={value || ""}
      onChange={onChange}
      onBlur={onBlur}
      options={items}
      resolvedOption={resolvedOption}
      onSearch={handleSearch}
      onLoadMore={handleLoadMore}
      onOpen={handleOpen}
      loading={loading}
      loadingMore={loadingMore}
      hasMore={hasMore}
      required={required}
      error={displayError}
      touched={touched}
      disabled={disabled}
      helperText={helperText}
      placeholder="Search MCC code or description…"
      noOptionsText="No MCC matches. Try another search."
      sx={sx}
    />
  );
};

export default MccSelectField;
